import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Button,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Divider,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { FiSave, FiRefreshCw } from "react-icons/fi";
import { useOvertimeSettings, useUpdateOvertimeSettings } from "../../../services/adminOvertimeService";

const OvertimeSettings = () => {
  const [formData, setFormData] = useState({
    max_weekly_overtime: 10.0,
    max_monthly_overtime: 40.0,
    overtime_rate: 1.5,
    approval_required: true,
    auto_approve_under: 2.0,
    require_reason: true,
  });

  const toast = useToast();

  // Load current settings
  const {
    settingsData,
    settingsError,
    settingsIsLoading,
    refetchSettings
  } = useOvertimeSettings();

  // Update settings
  const {
    updateSettings,
    updateSettingsIsLoading,
    updateSettingsError
  } = useUpdateOvertimeSettings(() => {
    toast({
      title: "Settings Updated",
      description: "Overtime settings have been successfully updated.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    refetchSettings();
  });

  // Update form data when settings load
  React.useEffect(() => {
    if (settingsData && Object.keys(settingsData).length > 0) {
      setFormData({
        max_weekly_overtime: settingsData.max_weekly_overtime || 10.0,
        max_monthly_overtime: settingsData.max_monthly_overtime || 40.0,
        overtime_rate: settingsData.overtime_rate || 1.5,
        approval_required: settingsData.approval_required !== false,
        auto_approve_under: settingsData.auto_approve_under || 2.0,
        require_reason: settingsData.require_reason !== false,
      });
    }
  }, [settingsData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    updateSettings(formData);
  };

  const handleReset = () => {
    if (settingsData && Object.keys(settingsData).length > 0) {
      setFormData({
        max_weekly_overtime: settingsData.max_weekly_overtime || 10.0,
        max_monthly_overtime: settingsData.max_monthly_overtime || 40.0,
        overtime_rate: settingsData.overtime_rate || 1.5,
        approval_required: settingsData.approval_required !== false,
        auto_approve_under: settingsData.auto_approve_under || 2.0,
        require_reason: settingsData.require_reason !== false,
      });
    }
  };

  if (settingsIsLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4}>Loading overtime settings...</Text>
      </Box>
    );
  }

  if (settingsError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Error Loading Settings</AlertTitle>
        <AlertDescription>
          {settingsError.message || "Failed to load overtime settings. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Error Alert */}
      {updateSettingsError && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Update Failed</AlertTitle>
          <AlertDescription>
            {updateSettingsError.message || "Failed to update settings. Please try again."}
          </AlertDescription>
        </Alert>
      )}

      {/* Overtime Limits */}
      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontSize="lg" fontWeight="600" color="gray.800" mb={2}>
                Overtime Limits
              </Text>
              <Text fontSize="sm" color="gray.600">
                Set maximum overtime hours allowed per week and month
              </Text>
            </Box>
            
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl>
                <FormLabel>Maximum Weekly Overtime (hours)</FormLabel>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.max_weekly_overtime}
                  onChange={(e) => handleInputChange('max_weekly_overtime', parseFloat(e.target.value))}
                  placeholder="10.0"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Maximum Monthly Overtime (hours)</FormLabel>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={formData.max_monthly_overtime}
                  onChange={(e) => handleInputChange('max_monthly_overtime', parseFloat(e.target.value))}
                  placeholder="40.0"
                />
              </FormControl>
            </SimpleGrid>
          </VStack>
        </CardBody>
      </Card>

      {/* Overtime Rate */}
      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontSize="lg" fontWeight="600" color="gray.800" mb={2}>
                Overtime Rate
              </Text>
              <Text fontSize="sm" color="gray.600">
                Set the overtime pay rate multiplier (e.g., 1.5 = time and a half)
              </Text>
            </Box>
            
            <FormControl>
              <FormLabel>Overtime Rate Multiplier</FormLabel>
              <Input
                type="number"
                step="0.1"
                min="1.0"
                value={formData.overtime_rate}
                onChange={(e) => handleInputChange('overtime_rate', parseFloat(e.target.value))}
                placeholder="1.5"
              />
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Approval Settings */}
      <Card>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <Text fontSize="lg" fontWeight="600" color="gray.800" mb={2}>
                Approval Settings
              </Text>
              <Text fontSize="sm" color="gray.600">
                Configure how overtime requests are processed
              </Text>
            </Box>
            
            <VStack spacing={4} align="stretch">
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="approval-required" mb="0">
                  Require Approval for Overtime
                </FormLabel>
                <Switch
                  id="approval-required"
                  isChecked={formData.approval_required}
                  onChange={(e) => handleInputChange('approval_required', e.target.checked)}
                />
              </FormControl>

              {formData.approval_required && (
                <FormControl>
                  <FormLabel>Auto-approve under (hours)</FormLabel>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.auto_approve_under}
                    onChange={(e) => handleInputChange('auto_approve_under', parseFloat(e.target.value))}
                    placeholder="2.0"
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Requests under this threshold will be automatically approved
                  </Text>
                </FormControl>
              )}

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="require-reason" mb="0">
                  Require Reason for Overtime
                </FormLabel>
                <Switch
                  id="require-reason"
                  isChecked={formData.require_reason}
                  onChange={(e) => handleInputChange('require_reason', e.target.checked)}
                />
              </FormControl>
            </VStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardBody>
          <HStack spacing={4} justify="flex-end">
            <Button
              leftIcon={<FiRefreshCw />}
              variant="outline"
              onClick={handleReset}
              isDisabled={updateSettingsIsLoading}
            >
              Reset
            </Button>
            <Button
              leftIcon={<FiSave />}
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={updateSettingsIsLoading}
              loadingText="Saving..."
            >
              Save Settings
            </Button>
          </HStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default OvertimeSettings; 